import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  LadderedTradeRequest,
  TradePreviewResponse,
  CancelTradeGroupRequest,
  CancelTradeGroupResponse ,
  MomentumSwingRequest,
  VolatilityLadderTradeRequest,
  BollingerReversionRequest,
  RsiExtremesRequest,
  MacdReversionRequest,
  MacdMomentumRequest,
  VwapReversionRequest,
  IchimokuCloudRequest,
  IchimokuEntryConditionsResponse,
  PivotPointTradingRequest,
  MaCrossoverRequest,
  SupportRetestDipLadderRequest,
  OrchestratedLadderRequest
} from '../types/tradeGroup';

/**
 * Get the current user's authentication token
 */
async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

/**
 * Generic API fetch wrapper with timeout and error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_CONFIG.tradeGroupBaseUrl}${endpoint}`;

  // Check if we're using a placeholder URL (local development)
  if (API_CONFIG.tradeGroupBaseUrl.includes('your-api-gateway-url') || API_CONFIG.tradeGroupBaseUrl === 'http://localhost:3000') {
    console.error('Trade Groups API not configured. Please set REACT_APP_TRADE_GROUP_API_BASE_URL environment variable.');
    throw new Error('API endpoint not configured. Please deploy the API or set REACT_APP_TRADE_GROUP_API_BASE_URL.');
  }

  // Get authentication token
  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...options?.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

/** Create or preview a laddered trade group.
 * When preview=true in request, returns TradePreviewResponse.
 * When preview=false, creates the trade group and returns { tradeGroupId: string }.
 */
export async function createLadderedTradeGroup(
  request: LadderedTradeRequest
): Promise<TradePreviewResponse | { tradeGroupId: string }> {
  console.log('Creating/previewing trade group with request:', request);
  const response = await apiFetch<TradePreviewResponse | { tradeGroupId: string }>('/v1/trade-groups/laddered', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  console.log('Trade group response:', response);
  return response;
}

/**
 * Create a momentum swing trade group
 */
export async function createMomentumSwingTradeGroup(
  request: MomentumSwingRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating momentum swing trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    quote_size: request.quoteSize,
    name: request.name,
    ...(request.origin && { origin: request.origin }),
    ...(request.preview !== undefined && { preview: request.preview }),
    ...(request.momentumStrength && { momentum_strength: request.momentumStrength }),
    ...(request.expectedEntryPrice && { expected_entry_price: request.expectedEntryPrice }),
  };

  const response = await apiFetch<{ tradeGroupId: string }>('/v1/trade-groups/momentum-swing', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create momentum swing trade group response:', response);
  return response;
}

/**
 * Create or preview a volatility ladder trade group.
 * When preview=true in request, returns TradePreviewResponse.
 * When preview=false, creates the trade group and returns { tradeGroupId: string }.
 */
export async function createVolatilityLadderTradeGroup(
  request: VolatilityLadderTradeRequest
): Promise<TradePreviewResponse | { tradeGroupId: string }> {
  console.log('Creating/previewing volatility ladder trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    name: request.name,
    total_quote_size: request.totalQuoteSize,
    max_price: request.maxPrice, // Backend needs max_price for range calculation
    number_of_rungs: request.numLadders, // Backend uses number_of_rungs, UI uses num_ladders
    ladder_spacing_pct: request.ladderSpacingPct, // ✅ Use request value directly (from ATR auto-config)
    profit_target_pct: request.profitTargetPct, // Use direct field from request
    volatility_period: request.volatilityPeriod,
    initial_deployment_pct: request.initialDeploymentPct,
    low_volatility_threshold: request.lowVolatilityThreshold,
    high_volatility_threshold: request.highVolatilityThreshold,
    extreme_volatility_threshold: request.extremeVolatilityThreshold,
    low_volatility_position_multiplier: request.lowVolatilityPositionMultiplier,
    high_volatility_position_multiplier: request.highVolatilityPositionMultiplier,
    rebalance_sensitivity: request.rebalanceSensitivity,
    min_rebalance_interval_hours: request.minRebalanceIntervalHours,
    // Adaptive mode fields (AVL-US-009)
    adaptive_mode: request.adaptiveMode || false,
    ...(request.adaptiveMode && request.lowerBoundPrice && { lower_bound_price: request.lowerBoundPrice }),
    ...(request.adaptiveMode && request.upperBoundPrice && { upper_bound_price: request.upperBoundPrice }),
    keep_alive: request.keepAlive,
    trailing_stop_enabled: request.trailingStopEnabled,
    trailing_stop_percent: request.trailingStopPercent,
    ...(request.preview !== undefined && { preview: request.preview }),
    ...(request.origin && { origin: request.origin }),
  };

  console.log('Transformed backend request:', backendRequest);

  const response = await apiFetch<TradePreviewResponse | { trade_group_id: string }>('/v1/volatility-ladder-trade', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Volatility ladder trade group response:', response);

  // If it's a preview response, return as-is (already in correct format)
  if ('previews' in response || 'totalTrades' in response) {
    return response as TradePreviewResponse;
  }

  // Handle create response - support both camelCase and snake_case field names
  const createResponse = response as { tradeGroupId?: string; trade_group_id?: string };
  const tradeGroupId = createResponse.tradeGroupId || createResponse.trade_group_id;

  if (!tradeGroupId) {
    console.error('No tradeGroupId found in response:', response);
    throw new Error('Invalid response: missing tradeGroupId');
  }

  return { tradeGroupId };
}

/**
 * Create a Bollinger Mean Reversion trade group
 * Requires productId, name, and quoteSize. Other config comes from global config.
 */
export async function createBollingerReversionTradeGroup(
  request: BollingerReversionRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating Bollinger Reversion trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true if not specified
    strategy_metric_period: request.strategyMetricPeriod || 'MEDIUM', // Send period to backend
  };

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/bollinger-reversion', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create Bollinger Reversion trade group response:', response);

  // Transform snake_case to camelCase for UI
  return {
    tradeGroupId: response.trade_group_id
  };
}

export async function createRsiExtremesTradeGroup(
  request: RsiExtremesRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating RSI Extremes trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true if not specified
    strategy_metric_period: request.strategyMetricPeriod || 'MEDIUM', // Send period to backend
  };

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/rsi-extremes', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create RSI Extremes trade group response:', response);

  // Transform snake_case to camelCase for UI
  return {
    tradeGroupId: response.trade_group_id
  };
}

/**
 * Create a MACD Mean Reversion trade group
 * Requires productId, name, and quoteSize. Other config comes from global config.
 */
export async function createMacdReversionTradeGroup(
  request: MacdReversionRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating MACD Reversion trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true if not specified
    strategy_metric_period: request.strategyMetricPeriod || 'MEDIUM', // Send period to backend
  };

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/macd-reversion', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create MACD Reversion trade group response:', response);

  // Transform snake_case to camelCase for UI
  return {
    tradeGroupId: response.trade_group_id
  };
}

/**
 * Create a MACD Momentum trade group (REQ-015, REQ-016).
 * Crossover-momentum strategy (distinct from MACD Reversion). Maps camelCase →
 * snake_case before POST to /v1/trade-groups/macd-momentum. The create router
 * forwards the backend creator response VERBATIM (AD-008) — flat JSON, NO
 * {data, metadata} envelope. Numeric/BigDecimal fields stay STRINGS on the wire
 * (FIX-015) so the backend handler's string-coercion branch is exercised.
 * Sibling: createMacdReversionTradeGroup.
 */
export async function createMacdMomentumTradeGroup(
  request: MacdMomentumRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating MACD Momentum trade group with request:', request);

  // Transform camelCase to snake_case for backend. The allocation field is capital_allocation (the
  // backend DTO/router name); profit_target_pct/stop_loss_pct are NOT sent — they are config-tier.
  const backendRequest: Record<string, unknown> = {
    product_id: request.productId,
    name: request.name,
    capital_allocation: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true (gradual-rollout posture)
    strategy_metric_period: request.strategyMetricPeriod || 'MEDIUM',
    trailing_stop_enabled: request.trailingStopEnabled,
  };
  // Trailing-stop group-tier overrides are only meaningful when trailing is enabled; each is omitted
  // when blank so the backend resolves NULL = inherit (product → global). The wizard collects 0-100
  // PERCENTS but the backend stores fractions, so convert (e.g., "3" → "0.03").
  if (request.trailingStopEnabled) {
    const pctToFraction = (v?: string): string | undefined =>
      v !== undefined && v.trim() !== '' ? (parseFloat(v) / 100).toString() : undefined;
    const overrides: Record<string, string | undefined> = {
      trailing_stop_percent: pctToFraction(request.trailingStopPercent),
      trailing_update_threshold: pctToFraction(request.trailingUpdateThreshold),
      trailing_stop_limit_buffer_pct: pctToFraction(request.trailingStopLimitBufferPct),
      trailing_stop_activation_pct: pctToFraction(request.trailingStopActivationPct),
    };
    for (const [key, value] of Object.entries(overrides)) {
      if (value !== undefined) backendRequest[key] = value;
    }
  }

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/macd-momentum', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create MACD Momentum trade group response:', response);

  // Verbatim flat response (AD-008): read trade_group_id directly, no envelope unwrap.
  return {
    tradeGroupId: response.trade_group_id
  };
}

/**
 * Create a VWAP Mean Reversion trade group (TASK-036, REQ-024).
 * REQ-014: vwap_period and band_sigma persist on the trade group (per-TG config).
 * REQ-015 TGS-readiness: quote_size flows verbatim into original_quote_size.
 */
export async function createVwapReversionTradeGroup(
  request: VwapReversionRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating VWAP Reversion trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest: Record<string, unknown> = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true (gradual-rollout posture)
    strategy_metric_period: request.strategyMetricPeriod,
    vwap_period: request.vwapPeriod,
    band_sigma: request.bandSigma,
  };
  // Group-tier ATR-stop overrides: omit when undefined so the backend persists NULL =
  // "inherit" via the three-tier resolver (matches the Ichimoku omit-when-undefined pattern).
  if (request.atrStopEnabled !== undefined) backendRequest.atr_stop_enabled = request.atrStopEnabled;
  if (request.atrStopMultiplier !== undefined) backendRequest.atr_stop_multiplier = request.atrStopMultiplier;
  if (request.atrMaxStopPct !== undefined) backendRequest.atr_max_stop_pct = request.atrMaxStopPct;

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/vwap-reversion', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create VWAP Reversion trade group response:', response);

  // Transform snake_case to camelCase for UI (AD-005: backend returns flat JSON, no envelope)
  return {
    tradeGroupId: response.trade_group_id
  };
}

/**
 * Create an Ichimoku Cloud trade group (REQ-022, REQ-026).
 * Sibling: createVwapReversionTradeGroup. Manually maps camelCase → snake_case
 * before POST per wire-contracts.md Boundary 3. NO envelope unwrap (AD-008).
 */
export async function createIchimokuCloudTradeGroup(
  request: IchimokuCloudRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating Ichimoku Cloud trade group with request:', request);

  // Build snake_case body, omitting optional fields when not provided so the
  // backend falls back to its config-provider defaults.
  const backendRequest: Record<string, unknown> = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true (gradual-rollout posture)
    strategy_metric_period: 'MEDIUM', // Ichimoku is MEDIUM-only (1h) at launch — REQ-022
    stop_style: request.stopStyle,
  };
  if (request.tenkanPeriod !== undefined) backendRequest.tenkan_period = request.tenkanPeriod;
  if (request.kijunPeriod !== undefined) backendRequest.kijun_period = request.kijunPeriod;
  if (request.senkouBPeriod !== undefined) backendRequest.senkou_b_period = request.senkouBPeriod;
  if (request.chikouDisplacement !== undefined) backendRequest.chikou_displacement = request.chikouDisplacement;
  if (request.tkCrossLookback !== undefined) backendRequest.tk_cross_lookback = request.tkCrossLookback;
  if (request.positionSizePct !== undefined) backendRequest.position_size_pct = request.positionSizePct;
  if (request.maxHoldingHours !== undefined) backendRequest.max_holding_hours = request.maxHoldingHours;
  // ICS-006 ATR risk framing — REQ-001 / wire-contracts.md Boundary 3.
  // exit_mode is always sent (defaults to ICHIMOKU_SIGNALS). ATR multipliers omitted
  // when undefined so the backend treats them as semantically NULL.
  if (request.exitMode !== undefined) backendRequest.exit_mode = request.exitMode;
  if (request.atrStopMultiplier !== undefined) backendRequest.atr_stop_multiplier = request.atrStopMultiplier;
  if (request.atrTargetMultiplier !== undefined) backendRequest.atr_target_multiplier = request.atrTargetMultiplier;
  if (request.atrProfitLockMultiplier !== undefined) backendRequest.atr_profit_lock_multiplier = request.atrProfitLockMultiplier;
  if (request.atrProfitLockActivationMultiplier !== undefined) backendRequest.atr_profit_lock_activation_multiplier = request.atrProfitLockActivationMultiplier;
  if (request.atrPeriod !== undefined) backendRequest.atr_period = request.atrPeriod;

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/ichimoku-cloud', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create Ichimoku Cloud trade group response:', response);

  return {
    tradeGroupId: response.trade_group_id,
  };
}

/**
 * Create a Moving Average Crossover trade group (REQ-011, REQ-013).
 *
 * Sibling: createIchimokuCloudTradeGroup. Manually maps camelCase → snake_case
 * before POST per wire-contracts.md Boundary 1. Optional fields are omitted when
 * undefined so the backend falls back to its `ma.crossover.*` config-provider
 * defaults. BigDecimal-ish fields (quote_size, profit_target_pct,
 * initial_trailing_stop_pct, position_size_pct) stay STRINGS on the wire (ICS-006).
 * The create response is consumed FLAT (AD-004): reads `response.trade_group_id`
 * directly, NOT `response.data.trade_group_id`.
 */
export async function createMaCrossoverTradeGroup(
  request: MaCrossoverRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating MA Crossover trade group with request:', request);

  const backendRequest: Record<string, unknown> = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true (gradual-rollout posture)
  };
  if (request.strategyMetricPeriod !== undefined) backendRequest.strategy_metric_period = request.strategyMetricPeriod;
  if (request.maType !== undefined) backendRequest.ma_type = request.maType;
  if (request.fastPeriod !== undefined) backendRequest.fast_period = request.fastPeriod;
  if (request.slowPeriod !== undefined) backendRequest.slow_period = request.slowPeriod;
  if (request.profitTargetPct !== undefined) backendRequest.profit_target_pct = request.profitTargetPct;
  if (request.initialTrailingStopPct !== undefined) backendRequest.initial_trailing_stop_pct = request.initialTrailingStopPct;
  if (request.positionSizePct !== undefined) backendRequest.position_size_pct = request.positionSizePct;

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/ma-crossover', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create MA Crossover trade group response:', response);

  // FLAT response (AD-004): read trade_group_id directly, no envelope unwrap.
  return {
    tradeGroupId: response.trade_group_id,
  };
}

/**
 * Create a Support-Retest Dip Ladder trade group (TASK-036, REQ-021).
 *
 * Sibling: createMaCrossoverTradeGroup. Manually maps camelCase → snake_case before
 * POST to /v1/trade-groups/support-retest-dip-ladder. quote_size stays a STRING on the
 * wire (form numbers are strings — ICS-006). strategy_metric_period is REQUIRED on the
 * wire — the backend's validateInput throws 400 if it is absent, there is no server-side
 * default — so it is always sent verbatim. Six optional group-tier tunable overrides
 * (target_pct/break_threshold_fraction/floor_buffer_fraction — support_retest_tunable_config_tiers
 * REQ-006, wire-contracts.md Boundary 1 — plus the breakeven-trail trio
 * trailing_stop_enabled/trailing_stop_activation_basis/trailing_stop_distance_fraction,
 * support_retest_breakeven_trailing_stop REQ-007) are sent percent-to-fraction + omit-if-blank,
 * mirroring createMacdMomentumTradeGroup's pctToFraction pattern exactly. The create
 * response is consumed FLAT: reads `response.trade_group_id` directly, NOT
 * `response.data.trade_group_id`.
 */
export async function createSupportRetestDipLadderTradeGroup(
  request: SupportRetestDipLadderRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating Support-Retest Dip Ladder trade group with request:', request);

  const backendRequest: Record<string, unknown> = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true (gradual-rollout posture)
    strategy_metric_period: request.strategyMetricPeriod,
  };

  // Omit-if-blank: a blank override field is never sent as "" or null -- the key is
  // absent entirely, so the backend column stays NULL and the resolver reads "inherit".
  const pctToFraction = (v?: string): string | undefined =>
    v !== undefined && v.trim() !== '' ? (parseFloat(v) / 100).toString() : undefined;
  const overrides: Record<string, string | undefined> = {
    target_pct: pctToFraction(request.targetPct),
    break_threshold_fraction: pctToFraction(request.breakThresholdFraction),
    floor_buffer_fraction: pctToFraction(request.floorBufferFraction),
    trailing_stop_activation_basis: request.trailingStopActivationBasis,
    trailing_stop_distance_fraction: pctToFraction(request.trailingStopDistanceFraction),
  };
  for (const [key, value] of Object.entries(overrides)) {
    if (value !== undefined) backendRequest[key] = value;
  }
  // Sent separately from the string overrides above because it is a tri-state BOOLEAN: an explicit
  // false must reach the wire (the group opts out of a trail its product tier enables), so the guard
  // is `!== undefined`, never a falsy check.
  if (request.trailingStopEnabled !== undefined) {
    backendRequest.trailing_stop_enabled = request.trailingStopEnabled;
  }

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/support-retest-dip-ladder', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create Support-Retest Dip Ladder trade group response:', response);

  // FLAT response: read trade_group_id directly, no envelope unwrap.
  return {
    tradeGroupId: response.trade_group_id,
  };
}

/**
 * Flat creation response for the Pivot Point endpoint (AD-9 / wire-contracts.md
 * Boundary 2). NO `{data, metadata}` envelope — `apiFetch` does not unwrap, so the
 * UI reads `response.trade_group_id` directly.
 */
export interface PivotPointTradeCreateResponse {
  success: boolean;
  created: boolean;
  trade_group_id: string;
  product_id: string;
  state: string;
  trade_group_type: string;
  strategy_metric_period: string;
  pivot_timeframe: string;
  paper_trade_mode: boolean;
  message?: string;
}

/**
 * Create a Pivot Point Trading trade group (TASK-038, REQ-023).
 *
 * Sibling: createIchimokuCloudTradeGroup. This function is the SINGLE
 * camelCase→snake_case conversion point (the backend wins on field names) and the
 * SINGLE 0-100→decimal-fraction conversion point for the percentage fields
 * (stopLossPct, tierExitPcts.*): the UI renders/validates 0-100 percentages, but
 * the wire/backend use decimal fractions, so each percent is divided by 100 here.
 *
 * Returns the FLAT response (AD-9): reads `response.trade_group_id` directly,
 * NOT `response.data.trade_group_id`.
 */
export async function createPivotPointTradingTradeGroup(
  request: PivotPointTradingRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating Pivot Point Trading trade group with request:', request);

  // Build the snake_case wire body. Optional fields are omitted when not provided
  // so the backend falls back to its global pivot.point.* config defaults.
  const backendRequest: Record<string, unknown> = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true (gradual-rollout posture)
    enabled_support_levels: request.enabledSupportLevels, // clean JSON array on the wire
  };
  if (request.strategyMetricPeriod !== undefined) {
    backendRequest.strategy_metric_period = request.strategyMetricPeriod;
  }
  if (request.pivotTimeframe !== undefined) {
    backendRequest.pivot_timeframe = request.pivotTimeframe;
  }
  // 0-100 → decimal fraction. The UI accepts a percentage (e.g. "1.5" = 1.5%);
  // the wire/column expects the fraction (0.015). Divide by 100 here only.
  if (request.stopLossPct !== undefined && request.stopLossPct.trim() !== '') {
    backendRequest.stop_loss_pct = (parseFloat(request.stopLossPct) / 100).toString();
  }
  if (request.tieredExitEnabled !== undefined) {
    backendRequest.tiered_exit_enabled = request.tieredExitEnabled;
  }
  if (request.tierExitPcts !== undefined) {
    // Tier percentages are 0-100 in the UI and must sum to 100; convert each to the
    // decimal fraction the backend expects (0.50 / 0.30 / 0.20, summing to 1.0).
    backendRequest.tier_exit_pcts = {
      pivot: (parseFloat(request.tierExitPcts.pivot) / 100).toString(),
      r1: (parseFloat(request.tierExitPcts.r1) / 100).toString(),
      r2: (parseFloat(request.tierExitPcts.r2) / 100).toString(),
    };
  }

  const response = await apiFetch<PivotPointTradeCreateResponse>('/v1/trade-groups/pivot-point-trading', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create Pivot Point Trading trade group response:', response);

  // FLAT response (AD-9): read trade_group_id directly, no envelope unwrap.
  return {
    tradeGroupId: response.trade_group_id,
  };
}

/**
 * Create a Trade Group Orchestrator (ORCHESTRATED_LADDER) trade group
 * (TASK-032, REQ-001, REQ-032).
 *
 * Sibling: createMaCrossoverTradeGroup. Maps camelCase → snake_case before POST to
 * /v1/trade-groups/orchestrated-ladder. Per AD-008 the orchestrator is standalone and
 * carries NO strategy_metric_period — only product, name, capital allocation, and the
 * paper-trade toggle are set at create time; every other lever lives in
 * TradeGroupOrchestratorConfigProvider (config.global_config). quote_size stays a STRING
 * on the wire (form numbers are strings — ICS-006). The create response is consumed FLAT
 * (no {data, metadata} envelope): the backend creator returns { trade_group_id } verbatim.
 */
export async function createOrchestratedLadderTradeGroup(
  request: OrchestratedLadderRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating Orchestrated Ladder trade group with request:', request);

  const backendRequest = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true (gradual-rollout posture)
  };

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/orchestrated-ladder', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create Orchestrated Ladder trade group response:', response);

  // FLAT response: read trade_group_id directly, no envelope unwrap.
  return {
    tradeGroupId: response.trade_group_id,
  };
}

/**
 * Fetch Ichimoku entry/exit conditions for a trade group (REQ-028, REQ-029).
 * Returns flat snake_case JSON, NO envelope. Sibling: getVwapEntryConditions.
 */
export async function getIchimokuEntryConditions(
  tradeGroupId: string
): Promise<IchimokuEntryConditionsResponse> {
  return apiFetch<IchimokuEntryConditionsResponse>(
    `/v1/trade-groups/${tradeGroupId}/ichimoku-entry-conditions`
  );
}

/**
 * Cancel a trade group (full or partial)
 */
export async function cancelTradeGroup(
  request: CancelTradeGroupRequest
): Promise<CancelTradeGroupResponse> {
  console.log('Canceling trade group with request:', request);
  const response = await apiFetch<CancelTradeGroupResponse>('/v1/trade-groups/cancel', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  console.log('Cancel trade group response:', response);
  return response;
}

/**
 * Result of an operator pause/resume action (REQ-014).
 * Backend returns a single-resource `{data, metadata}` envelope; apiFetch does NOT
 * auto-unwrap, so these functions read and return `response.data`.
 */
export interface OperatorPauseResumeResult {
  tradeGroupId: string;
  state: string; // OPERATOR_PAUSED after pause, WATCHING after resume
}

/**
 * Operator-pause a trade group (REQ-003, REQ-014).
 * Transitions the group to OPERATOR_PAUSED; the optional reason is persisted as the
 * operator's note. Returns the unwrapped `data` from the single-resource envelope.
 */
export async function operatorPauseTradeGroup(
  tradeGroupId: string,
  reason?: string
): Promise<OperatorPauseResumeResult> {
  const body: { tradeGroupId: string; reason?: string } = { tradeGroupId };
  if (reason && reason.trim()) {
    body.reason = reason.trim();
  }
  const response = await apiFetch<{ data: OperatorPauseResumeResult; metadata: unknown }>(
    '/v1/trade-groups/operator-pause',
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
  return response.data;
}

/**
 * Operator-resume an OPERATOR_PAUSED trade group (REQ-004, REQ-014).
 * Synchronous OPERATOR_PAUSED → WATCHING transition. Returns the unwrapped `data`.
 */
export async function operatorResumeTradeGroup(
  tradeGroupId: string
): Promise<OperatorPauseResumeResult> {
  const response = await apiFetch<{ data: OperatorPauseResumeResult; metadata: unknown }>(
    '/v1/trade-groups/operator-resume',
    {
      method: 'POST',
      body: JSON.stringify({ tradeGroupId }),
    }
  );
  return response.data;
}

/**
 * Fetch active trade groups with details
 */
export async function getActiveTradeGroupsDetails(): Promise<ActiveTradeGroupsDetailsResponse> {
  return apiFetch<ActiveTradeGroupsDetailsResponse>('/v1/trade-groups?limit=250');
}

/**
 * Fetch trade group details including all orders
 */
export async function getTradeGroupDetails(tradeGroupId: string): Promise<TradeGroupDetailsResponse> {
  return apiFetch<TradeGroupDetailsResponse>(`/v1/trade-groups/${tradeGroupId}`);
}

/**
 * Bollinger entry conditions response - real-time technical indicator values
 */
export interface BollingerEntryConditionsResponse {
  product_id: string;
  strategy_metric_period: string;
  granularity_mins: number;
  current_price: number | null;
  bollinger_upper: number | null;
  bollinger_middle: number | null;
  bollinger_lower: number | null;
  band_width: number | null;
  price_position_pct: number | null;  // 0% = at lower, 50% = at middle, 100% = at upper
  rsi: number | null;
  market_regime: string | null;
  trend_strength: number | null;
  relative_volume: number | null;
  s2_pivot_level: number | null;
  pivot_timeframe: string | null;
  entry_conditions: {
    is_price_at_lower_band: boolean;
    is_rsi_oversold: boolean;
    is_ranging_market: boolean;
    is_volume_normal: boolean;
    is_above_pivot_support: boolean;
    all_conditions_met: boolean;
    conditions_met_count: number;
  };
  timestamp: string;
}

/**
 * Fetch Bollinger entry conditions for a trade group
 * Returns real-time technical indicator values and entry condition status
 */
export async function getBollingerEntryConditions(tradeGroupId: string): Promise<BollingerEntryConditionsResponse> {
  return apiFetch<BollingerEntryConditionsResponse>(`/v1/trade-groups/${tradeGroupId}/entry-conditions`);
}

/**
 * RSI Extremes entry conditions response - real-time technical indicator values
 */
export interface RsiExtremesEntryConditionsResponse {
  product_id: string;
  strategy_metric_period: string;
  granularity_mins: number;
  current_price: number | null;
  rsi: number | null;
  rsi_oversold_threshold: number | null;
  market_regime: string | null;
  volatility_state: string | null;
  trend_strength: number | null;
  volatility_percentile: number | null;
  relative_volume: number | null;
  entry_conditions: {
    is_rsi_oversold: boolean;
    is_volatility_acceptable: boolean;
    is_ranging_market: boolean;
    is_weak_trend: boolean;
    is_volatility_percentile_ok: boolean;
    is_volume_normal: boolean;
    all_conditions_met: boolean;
    conditions_met_count: number;
  };
  timestamp: string;
}

/**
 * Fetch RSI Extremes entry conditions for a trade group
 * Returns real-time technical indicator values and entry condition status
 */
export async function getRsiExtremesEntryConditions(tradeGroupId: string): Promise<RsiExtremesEntryConditionsResponse> {
  return apiFetch<RsiExtremesEntryConditionsResponse>(`/v1/trade-groups/${tradeGroupId}/rsi-entry-conditions`);
}

/**
 * MACD entry conditions response - real-time technical indicator values
 */
export interface MacdEntryConditionsResponse {
  product_id: string;
  strategy_metric_period: string;
  granularity_mins: number;
  current_price: number | null;
  macd_line: number | null;
  signal_line: number | null;
  histogram: number | null;
  previous_histogram: number | null;
  rsi: number | null;
  market_regime: string | null;
  trend_hostility: number | null;
  volatility_percentile: number | null;
  volatility_state: string | null;
  s2_pivot_level: number | null;
  pivot_timeframe: string | null;
  entry_conditions: {
    is_histogram_recovering: boolean;
    is_macd_below_signal: boolean;
    is_rsi_moderately_oversold: boolean;
    is_regime_favorable: boolean;
    is_volatility_acceptable: boolean;
    is_above_pivot_support: boolean;
    all_conditions_met: boolean;
    conditions_met_count: number;
  };
  timestamp: string;
}

/**
 * Fetch MACD entry conditions for a trade group
 * Returns real-time technical indicator values and entry condition status
 */
export async function getMacdEntryConditions(tradeGroupId: string): Promise<MacdEntryConditionsResponse> {
  return apiFetch<MacdEntryConditionsResponse>(`/v1/trade-groups/${tradeGroupId}/macd-entry-conditions`);
}

/**
 * VWAP entry conditions response — real-time σ-scaled VWAP indicator values.
 * Wire shape per PLAN-022 + PLAN-AMEND-006 (entry_conditions.is_above_vwap added).
 * Flat envelope (AD-005); no {data, metadata} wrapper.
 */
export interface VwapEntryConditionsResponse {
  product_id: string;
  strategy_metric_period: string;
  granularity_mins: number;
  current_price: number | null;
  vwap: number | null;
  vwap_upper_band: number | null;
  vwap_lower_band: number | null;
  vwap_deviation_pct: number | null;
  band_sigma: number | null;
  vwap_period: number | null;
  entry_conditions: {
    is_below_lower_band: boolean;
    is_above_vwap: boolean;
    conditions_met_count: number;
  };
  timestamp: string;
}

/**
 * Fetch VWAP entry conditions for a trade group (TASK-037, REQ-020).
 * The backend handler applies σ-band scaling at read time using the trade group's
 * band_sigma — the values returned here match what VwapReversionService uses for
 * actual entry decisions.
 */
export async function getVwapEntryConditions(tradeGroupId: string): Promise<VwapEntryConditionsResponse> {
  return apiFetch<VwapEntryConditionsResponse>(`/v1/trade-groups/${tradeGroupId}/vwap-entry-conditions`);
}

/**
 * Pivot Point entry/exit conditions response (UI-PPEC-001). One endpoint serves both the
 * entry-conditions bar and the proximity exit bar. Flat envelope (AD-005); no {data, metadata}.
 *
 * `entry_conditions` booleans are nullable to carry the neutral/unknown state: an input that is
 * genuinely unavailable is `null` (not `false`, which would read as "condition failed"). The ambient
 * conditions (RSI/volume/regime) and the proximity fields populate continuously; `confidence_score`
 * is `null` until a fresh bounce exists.
 */
export interface PivotPointEntryConditionsResponse {
  product_id: string;
  strategy_metric_period: string;
  granularity_mins: number;
  pivot_timeframe: string;
  current_price: number | null;
  rsi: number | null;
  relative_volume: number | null;
  regime: string | null;
  nearest_support_level: string | null;
  nearest_support_distance_pct: number | null;
  entry_conditions: {
    is_signal_strength_ok: boolean | null;
    is_rsi_oversold: boolean | null;
    is_volume_surge: boolean | null;
    is_favorable_regime: boolean | null;
    is_confirmed_bounce: boolean | null;
    conditions_met_count: number;
  };
  has_bounce_candidate: boolean;
  signal_strength: number | null;
  confidence_score: number | null;
  min_confidence_score: number;
  is_cooldown_active: boolean;
  in_position: boolean;
  stop_loss_price: number | null;
  target_pivot: number | null;
  target_r1: number | null;
  target_r2: number | null;
  partial_exit_completed: boolean | null;
  second_partial_completed: boolean | null;
  timestamp: string;
}

/**
 * Fetch Pivot Point entry/exit conditions for a trade group (UI-PPEC-001). The backend reimplements
 * the strategy's own filter/confidence evaluation (guarded byte-equal against PivotPointDecisionEngine),
 * so the displayed states match what PivotPointTradingService uses to gate entry/exit.
 */
export async function getPivotPointEntryConditions(
  tradeGroupId: string,
): Promise<PivotPointEntryConditionsResponse> {
  return apiFetch<PivotPointEntryConditionsResponse>(
    `/v1/trade-groups/${tradeGroupId}/pivot-point-entry-conditions`,
  );
}

/**
 * Response type for active trade groups count
 */
export interface ActiveTradeGroupsResponse {
    activeTradeGroupsCount: number;
    timestamp: number;
}

/**
 * Trade group details
 */
export interface TradeGroup {
    tradeGroupId: string;
    name: string;
    productId: string;
    state: string;
    tradeGroupType: string;
    isActive: boolean;
    currentPriceStatus: string;
    origin: string;
    buyPriceRangePct: number;
    minBuyPrice: number;
    maxBuyPrice: number;
    currentPrice: number;
    sellPriceRangePct: number;
    minSellPrice: number;
    maxSellPrice: number;
    totalCapturedProfit: number;
    avgCapturedReturn: number;
    openSellOrders: number;
    openSellCostBasis: number;
    openSellTargetProfit: number;        // Potential P&L at sell target prices
    openSellUnrealizedProfit: number;    // Actual unrealized P&L at current market price
    combinedPnl: number;                 // Total P&L (Realized + Unrealized)
    openBuyOrders: number;
    openBuyCostBasis: number;
    openBuyUnrealizedProfit: number;
    netHoldingsCostBasis: number;         // Cost basis of currently held, not-yet-sold position
    totalOpenOrders: number;
    totalFees: number;
    keepAliveCount: number;
    createdAt: number | null;
    lastModified: number | null;
    originalQuoteSize?: number;  // Total allocated capital for this trade group
    currentQuoteSize?: number;   // Remaining allocated capital
    // Volatility Ladder specific fields (legacy - kept for backward compatibility)
    volatilityRegime?: string;  // LOW, NORMAL, HIGH, EXTREME
    volatilityPercentile?: number;  // 0-1.0
    currentPositionMultiplier?: number;  // Calculated multiplier based on regime
    capitalDeployedPct?: number;  // 0-1.0
    lastRebalanceTime?: number | null;  // Timestamp
    lastVolatilityCheck?: number | null;  // Timestamp
    volatilityPeriod?: string;  // VERY_SHORT, SHORT, MEDIUM, etc.
    // Bollinger Reversion specific fields
    bollingerCycleCount?: number;
    bollingerWinCount?: number;
    bollingerLossCount?: number;
    bollingerWinRate?: number;  // Calculated: winCount / (winCount + lossCount), 0.0-1.0
    bollingerCumulativePnl?: number;
    bollingerStrategyMetricPeriod?: string;
    bollingerEntryPrice?: number;
    bollingerEntryRsi?: number;
    bollingerEntryQuoteSize?: number;
    bollingerLowerBand?: number;
    bollingerMiddleBand?: number;
    bollingerUpperBand?: number;
    bollingerPrimaryTarget?: number;
    bollingerExtendedTarget?: number;
    bollingerStopLoss?: number;
    bollingerMarketRegime?: string;
    bollingerEntryTime?: number | null;
    bollingerS2Pivot?: number;
    bollingerPartialExitCompleted?: boolean;
    bollingerPartialExitTime?: number | null;
    bollingerPartialExitPrice?: number;
    // RSI Extremes specific fields
    rsiCycleCount?: number;
    rsiWinCount?: number;
    rsiLossCount?: number;
    rsiWinRate?: number;  // Calculated: winCount / (winCount + lossCount), 0.0-1.0
    rsiCumulativePnl?: number;
    rsiStrategyMetricPeriod?: string;  // VERY_SHORT, SHORT, MEDIUM_SHORT, MEDIUM
    rsiEntryPrice?: number;
    rsiEntryRsi?: number;
    rsiEntryQuoteSize?: number;
    rsiMarketRegime?: string;  // RANGING, TRENDING, etc.
    rsiVolatilityState?: string;  // LOW, MEDIUM, HIGH, EXTREME
    rsiVolatilityPercentile?: number;
    rsiTrendStrength?: number;
    rsiOverboughtThreshold?: number;  // e.g., 70
    rsiOversoldThreshold?: number;  // e.g., 30
    rsiProfitTarget?: number;
    rsiStopLoss?: number;
    rsiTrailingStopActivationPrice?: number;
    rsiTrailingStopDistance?: number;
    rsiCurrentRsi?: number;  // Current RSI level when IN_POSITION
    rsiEntryTime?: number | null;
    rsiPartialExitCompleted?: boolean;
    rsiPartialExitTime?: number | null;
    rsiPartialExitPrice?: number;
    rsiStateChangeReason?: string;  // Human-readable explanation of current state (e.g., "Cycle complete: P&L=0.02, Winner=true")
    // MACD Reversion specific fields
    macdCycleCount?: number;
    macdWinCount?: number;
    macdLossCount?: number;
    macdWinRate?: number;  // Calculated: winCount / (winCount + lossCount), 0.0-1.0
    macdCumulativePnl?: number;
    macdStrategyMetricPeriod?: string;  // VERY_SHORT, SHORT, MEDIUM_SHORT, MEDIUM
    macdEntryPrice?: number;
    macdEntryTime?: number | null;
    macdEntryMacdLine?: number;
    macdEntrySignalLine?: number;
    macdEntryHistogram?: number;
    macdStopLoss?: number;
    macdProfitTarget?: number;
    macdStateChangeReason?: string;
    // MACD Momentum specific fields (macdMomentum-prefixed keys per backend
    // getMacdMomentumDetails projection; crossover-momentum strategy)
    macdMomentumCycleCount?: number;
    macdMomentumWinCount?: number;
    macdMomentumLossCount?: number;
    macdMomentumWinRate?: number;  // Calculated: winCount / (winCount + lossCount), 0.0-1.0
    macdMomentumCumulativePnl?: number;
    macdMomentumStrategyMetricPeriod?: string;
    macdMomentumEntryPrice?: number;
    macdMomentumEntryTime?: number | null;
    macdMomentumEntryMacdLine?: number;
    macdMomentumEntrySignalLine?: number;
    macdMomentumEntryHistogram?: number;
    macdMomentumEntryRegime?: string;
    macdMomentumEntryRsi?: number;
    macdMomentumEntryConfidenceScore?: number;
    macdMomentumStopLoss?: number;
    macdMomentumProfitTarget?: number;
    macdMomentumTrailingStopEnabled?: boolean;
    macdMomentumTrailingStopActivationPrice?: number;
    macdMomentumPeakPrice?: number;
    macdMomentumStateChangeReason?: string;
    // VWAP Reversion specific fields (TASK-025, vwap-prefixed keys per backend
    // getVwapReversionDetails in TradeGroupApiHandler)
    vwapCycleCount?: number;
    vwapWinCount?: number;
    vwapLossCount?: number;
    vwapWinRate?: number;
    vwapCumulativePnl?: number;
    vwapStrategyMetricPeriod?: string;
    vwapPeriod?: number;          // Rolling VWAP window (REQ-014)
    vwapBandSigma?: number;       // σ-band multiplier (REQ-014)
    vwapCycleId?: string;
    vwapCycleNumber?: number;
    vwapCycleState?: string;
    vwapEntryPrice?: number;
    vwapEntryTime?: number | null;
    vwapEntryQuoteSize?: number;
    vwapEntryFilledSize?: number;
    vwapEntryVwap?: number;
    vwapEntryUpperBand?: number;
    vwapEntryLowerBand?: number;
    vwapEntryBandWidth?: number;
    vwapEntryVwapDeviationPct?: number;
    vwapStopLoss?: number;
    /** Stop-loss LIMIT price actually on the broker (coinbase_trade_order_log.limit_price);
     *  = trigger × STOP_LIMIT_SLIPPAGE at submit time. Pulled byte-identical to broker. */
    vwapStopLossLimit?: number;
    /** Stop basis the cycle actually used: StopBasis enum name ("ATR"/"FLAT_FALLBACK"), or null. */
    vwapStopBasis?: string | null;
    vwapEffectiveAtrMultiplier?: number | null;
    /** Tier the ATR-stop config resolved at: ConfigResolutionTier enum name ("GROUP"/"PRODUCT"/"GLOBAL"), or null. */
    vwapEffectiveStopTier?: string | null;
    vwapExitTime?: number | null;
    vwapExitPrice?: number;
    vwapExitVwap?: number;
    vwapExitQuoteSize?: number;
    vwapPartialExitCompleted?: boolean;
    vwapPartialExitTime?: number | null;
    vwapPartialExitPrice?: number;
    vwapPartialExitFilledSize?: number;
    vwapPartialExitQuoteSize?: number;
    vwapPartialExitVwap?: number;
    vwapStateChangeReason?: string;
    // Pivot Point Trading specific fields (TASK-039b, pivot-prefixed keys per backend
    // getPivotPointDetails in TradeGroupApiHandler; merged flat via putAll)
    pivotCycleCount?: number;
    pivotWinCount?: number;
    pivotLossCount?: number;
    pivotWinRate?: number;  // Calculated: winCount / (winCount + lossCount), 0.0-1.0
    pivotCumulativePnl?: number;
    pivotStrategyMetricPeriod?: string;
    pivotGranularityMins?: number;
    // Per-group config (pivot_point_trade_group outrigger)
    pivotTimeframe?: string;  // PivotTimeframe enum name (e.g. TWENTY_FOUR_HOUR)
    pivotTradeS1?: boolean;
    pivotTradeS2?: boolean;
    pivotTradeS3?: boolean;
    pivotTieredExitEnabled?: boolean;
    pivotTierExitPctPivot?: number;  // Decimal fraction (e.g. 0.50) — render *100 for display
    pivotTierExitPctR1?: number;     // Decimal fraction
    pivotTierExitPctR2?: number;     // Decimal fraction
    pivotStopLossPct?: number;       // Decimal fraction
    // Current cycle (populated when in position)
    pivotCycleId?: string;
    pivotCycleNumber?: number;
    pivotCycleState?: string;
    pivotEntryTime?: number | null;
    pivotEntryPrice?: number | null;
    pivotEntryQuoteSize?: number | null;
    pivotEntryFilledSize?: number | null;
    pivotStopLossPrice?: number | null;
    // Entry attribution (pivot_point_cycle_context)
    pivotEntryLevelType?: string | null;  // PivotLevelType (S1/S2/S3)
    pivotEntrySignalStrength?: number | null;
    pivotEntryRsi?: number | null;
    pivotEntryMarketRegime?: string | null;
    pivotConfidenceScore?: number | null;
    // Resolved three-tier exit targets
    pivotTargetPivot?: number | null;
    pivotTargetR1?: number | null;
    pivotTargetR2?: number | null;
    // Three-tier exit progress
    pivotPartialExitCompleted?: boolean;
    pivotPartialExitTime?: number | null;
    pivotPartialExitPrice?: number | null;
    pivotSecondPartialCompleted?: boolean;
    pivotSecondPartialTime?: number | null;
    pivotSecondPartialPrice?: number | null;
    // Pause/Resume tracking fields (RSI-PAUSE-001)
    pauseCount?: number;  // Total number of times this trade group has been paused
    lastPauseTime?: number | null;  // Timestamp of most recent pause
    lastPauseReason?: string | null;  // Detailed reason for last pause
    lastResumeTime?: number | null;  // Timestamp of most recent resume
    lastResumeReason?: string | null;  // Detailed reason for last resume
    peakCumulativePnl?: number | null;  // Peak cumulative P&L for drawdown calculation
    peakPnlTime?: number | null;  // Timestamp when peak was reached
    // Volatility Ladder complete details (new format)
    volatilityLadder?: VolatilityLadderDetails;
    // Ichimoku Cloud strategy details (nested, populated for ICHIMOKU_CLOUD trade groups
    // via TradeGroupApiHandler.getIchimokuCloudDetails — Boundary 5)
    ichimokuDetails?: IchimokuTradeGroupDetails;
    // Orchestrated Ladder strategy details (nested, populated for ORCHESTRATED_LADDER trade
    // groups via TradeGroupApiHandler.mapOrchestratedLadderRow — TASK-031). The detail
    // endpoint returns {tradeGroup, orders, timestamp} (NOT a {data, metadata} envelope),
    // so this sub-object is read from tradeGroup.orchestratedLadder — the same nesting as
    // volatilityLadder/ichimokuDetails.
    orchestratedLadder?: OrchestratedLadderDetails;
}

/**
 * Trade Group Orchestrator (ORCHESTRATED_LADDER) outrigger details (TASK-031, TASK-032).
 *
 * camelCase keys mirroring the nested `orchestratedLadder` sub-object produced by
 * `TradeGroupApiHandler.mapOrchestratedLadderRow`. Surfaces the persisted lifecycle FSM
 * state, the managed child trade group id, the aggregate realized/unrealized PnL (decimal
 * fractions of the allocation), the last confluence score, and the deployed-config
 * snapshot. PnL values are decimal fractions — the ×100 rendering belongs only here in
 * the frontend.
 */
export interface OrchestratedLadderDetails {
    orchestratorState: string | null;          // Current-cycle cycle_state (OrchestratorState enum name: WATCHING/CHILD_TRADE_GROUP_ID_CREATED/CHILD_TRADE_GROUP_SUBMITTED/MANAGING/EXITING/CYCLE_COMPLETE/CYCLE_ABORTED)
    deployedChildTradeGroupId: string | null;  // Managed child ladder id of the current cycle (null while WATCHING)
    lastConfluenceScore: number | null;        // Most-recent confluence gate score
    confluenceEvaluatedAt: number | null;      // Timestamp (epoch millis)
    deploymentTime: number | null;             // Timestamp of the active deployment (epoch millis)
    aggregateRealizedPnl: number | null;       // Realized PnL as a fraction of allocation
    aggregateUnrealizedPnl: number | null;     // Unrealized (mark-to-market) PnL as a fraction of allocation
    pnlHighWaterMark: number | null;           // Peak aggregate PnL fraction (profit-lock reference)
    cooldownUntil: number | null;              // Timestamp cooldown elapses (epoch millis); lives on the durable group row
    lastExitReason: string | null;             // ExitReason (raw string) of the current cycle's last teardown
    deployedMinPrice: number | null;           // Deployed ladder price range (lower)
    deployedMaxPrice: number | null;           // Deployed ladder price range (upper)
    deployedNumRungs: number | null;           // Deployed rung count
    deployedQuoteDistributionType: string | null; // QuoteDistributionType enum name
    deployedCapitalAllocation: number | null;  // Deployed capital allocation (quote currency)
    cycles?: OrchestratedLadderCycleSummary[]; // Per-deployment cycle history, ordered cycle_number DESC ([] when the group has no cycles yet)
}

/**
 * One historical orchestrator cycle (one deployment attempt) as projected by
 * `TradeGroupApiHandler.mapOrchestratedLadderRow`'s `cycles` array. camelCase keys
 * mirroring an `order_book.orchestrated_ladder_cycle` row. PnL values are decimal
 * fractions of the allocation — the ×100 rendering belongs only here in the frontend.
 */
export interface OrchestratedLadderCycleSummary {
    cycleNumber: number;                       // Monotonic cycle index for the group
    cycleState: string;                        // OrchestratorState enum name for this cycle
    deployedChildTradeGroupId: string | null;  // Managed child ladder id (null while WATCHING)
    confluenceScore: number | null;            // Confluence gate score at deployment
    deploymentTime: number | null;             // Timestamp of the deployment (epoch millis)
    exitReason: string | null;                 // ExitReason (raw string) of the cycle teardown
    exitTime: number | null;                   // Timestamp the cycle exited (epoch millis)
    aggregateRealizedPnl: number | null;       // Realized PnL as a fraction of allocation
    aggregateUnrealizedPnl: number | null;     // Unrealized PnL as a fraction of allocation
}

/**
 * Ichimoku Cloud trade-group + most-recent-cycle context, as projected by
 * `TradeGroupApiHandler.getIchimokuCloudDetails`. camelCase keys.
 */
export interface IchimokuTradeGroupDetails {
  // Trade-group-level config
  granularityMins?: number;
  stopStyle?: string;
  positionSizePct?: number;
  cycleCount?: number;
  winCount?: number;
  lossCount?: number;
  cumulativePnl?: number;
  // Most-recent-cycle base (populated when at least one cycle exists)
  cycleId?: string;
  cycleNumber?: number;
  cycleState?: string;
  entryTime?: number | null;
  entryPrice?: number | null;
  entryQuoteSize?: number | null;
  entryFilledSize?: number | null;
  stopLossPrice?: number | null;
  exitTime?: number | null;
  exitPrice?: number | null;
  exitReason?: string | null;
  realizedPnl?: number | null;
  isWinner?: boolean | null;
  stateChangeReason?: string | null;
  // Ichimoku entry attribution (REQ-016)
  entryTenkanSen?: number | null;
  entryKijunSen?: number | null;
  entrySenkouSpanA?: number | null;
  entrySenkouSpanB?: number | null;
  entryChikouSpan?: number | null;
  entryCloudTop?: number | null;
  entryCloudBottom?: number | null;
  entrySignalComponents?: string | null;
  stopLossBasis?: number | null;
  // Exit attribution (REQ-019), populated once cycle is past EXIT_ORDER_FILLED
  exitTenkanSen?: number | null;
  exitKijunSen?: number | null;
  exitSenkouSpanA?: number | null;
  exitSenkouSpanB?: number | null;
  exitChikouSpan?: number | null;
  exitCloudTop?: number | null;
  exitCloudBottom?: number | null;
}

/**
 * Volatility Ladder specific attributes
 * Includes configuration and current state
 */
export interface VolatilityLadderDetails {
  // Configuration fields (static)
  targetOrderAmount: number | null;
  numberOfRungs: number | null;
  ladderSpacingPct: number | null;  // e.g., 0.0032 = 0.32%
  ladderSpacingAmount: number | null;  // Dollar amount between rungs
  volatilityPeriod: string | null;  // VERY_SHORT, SHORT, MEDIUM, etc.
  initialDeploymentPct: number | null;  // 0.15 = 15%
  profitTargetPct: number | null;  // 0.02 = 2%
  adaptiveMode: boolean;
  lowerBoundPrice: number | null;
  upperBoundPrice: number | null;
  
  // Threshold configuration
  lowVolatilityThreshold: number | null;
  highVolatilityThreshold: number | null;
  extremeVolatilityThreshold: number | null;
  lowVolatilityPositionMultiplier: number | null;
  highVolatilityPositionMultiplier: number | null;
  rebalanceSensitivity: number | null;
  minRebalanceIntervalHours: number | null;
  
  // Current state (dynamic)
  currentAtr: number | null;
  averageAtr: number | null;
  volatilityPercentile: number | null;  // 0-1.0
  previousVolatilityPercentile: number | null;
  currentVolatilityRegime: string | null;  // LOW, NORMAL, HIGH, EXTREME
  lastVolatilityCheck: number | null;  // Timestamp
  volatilityCheckIntervalHours: number | null;
  lastRebalanceTime: number | null;  // Timestamp
  capitalDeployedPct: number | null;  // 0-1.0
  lastCapitalDeployment: number | null;  // Timestamp
  lastDipBuyingTime: number | null;  // Timestamp
  candleCount: number | null;
}

/**
 * Response type for active trade groups details
 */
export interface ActiveTradeGroupsDetailsResponse {
    tradeGroups: TradeGroup[];
    count: number;
    timestamp: number;
}

/**
 * Trade order details
 */
export interface TradeOrder {
    clientOrderId: string;
    productId: string;
    side: 'BUY' | 'SELL';
    status: string;
    quoteSize: number;
    baseSize: number;
    limitPrice: number;
    price: number;
    postOnly: boolean;
    orderType: string;
    sellTargetPrice: number | null;
    sellTargetStatus: string | null;
    sellOrderId: string | null;
    sellClientOrderId: string | null;
    origin: string;
    keepAlive: boolean;
    expectedEntryPrice: number | null;
    creationTrigger: string | null;
    cancellationTrigger: string | null;
    createdAt: number | null;
    lastModified: number | null;
    orderId: string | null;
    filledSize: number | null;
    averageFilledPrice: number | null;
    fee: number | null;
    totalFees: number | null;
    totalValueAfterFees: number | null;
    rejectMessage: string | null;
    cancelMessage: string | null;
    coinbaseCreatedTime: number | null;
    lastFillTime: number | null;
}

/**
 * Response type for trade group details with orders
 */
export interface TradeGroupDetailsResponse {
    tradeGroup: TradeGroup;
    orders: TradeOrder[];
    timestamp: number;
}

/**
 * Adjustment log entry
 */
export interface AdjustmentLogEntry {
    logId: number;
    tradeGroupId: string;
    adjustmentType: 'RANGE_CREATED' | 'RANGE_AGED_OUT' | 'CAPITAL_REDEPLOYED';
    priceRangeMin: number | null;
    priceRangeMax: number | null;
    capitalAmount: number | null;
    reason: string;
    createdAt: number;
}

/**
 * Pagination metadata
 */
export interface PaginationMetadata {
    page: number;
    perPage: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

/**
 * Response type for trade group adjustments
 */
export interface TradeGroupAdjustmentsResponse {
    adjustments: AdjustmentLogEntry[];
    pagination: PaginationMetadata;
    timestamp: number;
}

/**
 * Fetch active trade groups count
 */
export async function getActiveTradeGroupsCount(): Promise<ActiveTradeGroupsResponse> {
    return apiFetch<ActiveTradeGroupsResponse>(API_CONFIG.endpoints.activeTradeGroups);
}

/**
 * Fetch adjustment log for a trade group
 * 
 * @param tradeGroupId - ID of the trade group
 * @param page - Page number (default 1)
 * @param perPage - Results per page (default 25)
 * @param adjustmentType - Filter by type (comma-separated for multiple)
 */
export async function getTradeGroupAdjustments(
    tradeGroupId: string,
    page: number = 1,
    perPage: number = 25,
    adjustmentType?: string
): Promise<TradeGroupAdjustmentsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('perPage', perPage.toString());
    if (adjustmentType) {
        params.append('adjustmentType', adjustmentType);
    }
    
    return apiFetch<TradeGroupAdjustmentsResponse>(
        `/v1/trade-groups/${tradeGroupId}/adjustments?${params.toString()}`
    );
}

/**
 * Ladder range entry
 */
export interface LadderRangeEntry {
    rangeId: string;
    tradeGroupId: string;
    rangeMinPrice: number;
    rangeMaxPrice: number;
    deployedCapital: number;
    rangeStatus: 'ACTIVE' | 'APPROACHING_AGEOUT' | 'AGED_OUT';
    createdAt: number;
    lastFillTime: number | null;
    ageOutThreshold: number;
    creationReason: string;
    lastModified: number;
}

/**
 * Ladder ranges summary statistics
 */
export interface LadderRangeSummary {
    totalRanges: number;
    activeRanges: number;
    totalDeployedCapital: number;
    averageRangeWidth: number;
}

/**
 * Response type for trade group ladder ranges
 */
export interface TradeGroupRangesResponse {
    ranges: LadderRangeEntry[];
    summary: LadderRangeSummary;
    timestamp: number;
}

/**
 * Fetch ladder ranges for a trade group
 * 
 * @param tradeGroupId - ID of the trade group
 * @param status - Filter by status (comma-separated for multiple)
 */
export async function getTradeGroupRanges(
    tradeGroupId: string,
    status?: string
): Promise<TradeGroupRangesResponse> {
    const params = new URLSearchParams();
    if (status) {
        params.append('status', status);
    }
    
    const url = status 
        ? `/v1/trade-groups/${tradeGroupId}/ranges?${params.toString()}`
        : `/v1/trade-groups/${tradeGroupId}/ranges`;
    
    return apiFetch<TradeGroupRangesResponse>(url);
}

/**
 * Timeline candle data for candlestick chart
 */
export interface TimelineCandle {
    timestamp: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

/**
 * Timeline event types
 */
export type TimelineEventType =
    | 'BUY_FILL'
    | 'SELL_FILL'
    | 'RANGE_CREATED'
    | 'RANGE_AGED_OUT'
    | 'CAPITAL_REDEPLOYED'
    | 'DIP_BUYING'
    | 'REBALANCE'
    | 'HOT_MONEY_REINVESTMENT';

/**
 * Timeline event for marking bot activity on chart
 */
export interface TimelineEvent {
    type: TimelineEventType;
    timestamp: number;
    price: number | null;
    amount: number | null;
    details: {
        orderId?: string;
        side?: string;
        filledSize?: number;
        fee?: number;
        origin?: string;
        logId?: number;
        adjustmentType?: string;
        priceRangeMin?: number;
        priceRangeMax?: number;
        reason?: string;
    };
}

/**
 * Trade pair representing a buy → sell lifecycle
 */
export interface TradePair {
    buyOrderId: string;
    sellOrderId: string | null;  // Null if sell not yet filled
    buyTimestamp: number;         // Epoch millis
    sellTimestamp: number | null; // Null if sell not yet filled
    buyPrice: number;
    sellPrice: number | null;     // Null if sell not yet filled
    profit: number | null;        // Null if sell not yet filled
    profitPercent: number | null; // Null if sell not yet filled
    creationTrigger?: string | null;      // e.g., INITIAL, DIP_BUY, HOT_MONEY, etc.
    cancellationTrigger?: string | null;  // Reason if cancelled
    orderStatus?: string | null;           // FILLED, OPEN, CANCELLED
}

/**
 * Response type for trade group timeline
 */
export interface TradeGroupTimelineResponse {
    candles: TimelineCandle[];
    events: TimelineEvent[];
    tradePairs: TradePair[];
    tradeGroupCreatedAt: number;
    granularity: number; // minutes
}

/**
 * Fetch timeline data for a trade group
 * 
 * @param tradeGroupId - ID of the trade group
 * @param startDate - Start date (epoch millis), defaults to 7 days ago
 * @param endDate - End date (epoch millis), defaults to now
 * @param granularity - Candle granularity in minutes (1, 5, 15, 60, 240, 1440), defaults to 60
 */
export async function getTradeGroupTimeline(
    tradeGroupId: string,
    startDate?: number,
    endDate?: number,
    granularity?: number
): Promise<TradeGroupTimelineResponse> {
    // Default values
    const now = Date.now();
    const defaultStartDate = now - (7 * 24 * 60 * 60 * 1000); // 7 days ago
    const defaultGranularity = 60; // 1 hour candles

    const params = new URLSearchParams();
    params.append('startDate', (startDate ?? defaultStartDate).toString());
    params.append('endDate', (endDate ?? now).toString());
    params.append('granularity', (granularity ?? defaultGranularity).toString());

    const url = `/v1/trade-groups/${tradeGroupId}/timeline?${params.toString()}`;

    return apiFetch<TradeGroupTimelineResponse>(url);
}
